// Beat detection utilities with WebGPU acceleration
import WebGPUProcessor from './webgpu.js';

const webgpuProcessor = new WebGPUProcessor();

export const analyzeAudioFeatures = async (channelData, sampleRate) => {
  const frameSize = 1024;
  const hopSize = 512;
  const features = [];

  // Try to use WebGPU for faster processing
  const gpuInitialized = await webgpuProcessor.initialize();
  
  if (gpuInitialized) {
    console.log('Using WebGPU for accelerated audio analysis');
    return await analyzeAudioFeaturesGPU(channelData, sampleRate);
  }

  console.log('Using CPU for audio analysis (WebGPU not available)');

  for (let i = 0; i + frameSize <= channelData.length; i += hopSize) {
    const frame = channelData.slice(i, i + frameSize);
    const timePosition = i / sampleRate;
    
    // Calculate RMS (Root Mean Square) for volume
    let rms = 0;
    for (let j = 0; j < frame.length; j++) {
      rms += frame[j] * frame[j];
    }
    rms = Math.sqrt(rms / frame.length);

    // Simple spectral centroid calculation
    let spectralSum = 0;
    let magnitudeSum = 0;
    
    for (let j = 0; j < frame.length / 2; j++) {
      const magnitude = Math.abs(frame[j]);
      spectralSum += j * magnitude;
      magnitudeSum += magnitude;
    }
    
    const spectralCentroid = magnitudeSum > 0 ? spectralSum / magnitudeSum : 0;

    features.push({
      time: timePosition,
      rms,
      spectralCentroid,
      frame
    });
  }

  return features;
};

// GPU-accelerated audio feature analysis
const analyzeAudioFeaturesGPU = async (channelData, sampleRate) => {
  const frameSize = 1024;
  const hopSize = 512;
  const features = [];

  for (let i = 0; i + frameSize <= channelData.length; i += hopSize) {
    const frame = channelData.slice(i, i + frameSize);
    const timePosition = i / sampleRate;
    
    // Use WebGPU FFT for spectral analysis
    const fftResult = await webgpuProcessor.computeFFT(frame);
    
    if (fftResult) {
      // Calculate RMS from frequency domain
      let rms = 0;
      let spectralSum = 0;
      let magnitudeSum = 0;
      
      for (let j = 0; j < fftResult.length / 2; j++) {
        const magnitude = fftResult[j].magnitude;
        rms += magnitude * magnitude;
        spectralSum += j * magnitude;
        magnitudeSum += magnitude;
      }
      
      rms = Math.sqrt(rms / (fftResult.length / 2));
      const spectralCentroid = magnitudeSum > 0 ? spectralSum / magnitudeSum : 0;

      features.push({
        time: timePosition,
        rms,
        spectralCentroid,
        frame,
        fftData: fftResult
      });
    } else {
      // Fallback to CPU calculation for this frame
      let rms = 0;
      for (let j = 0; j < frame.length; j++) {
        rms += frame[j] * frame[j];
      }
      rms = Math.sqrt(rms / frame.length);

      let spectralSum = 0;
      let magnitudeSum = 0;
      
      for (let j = 0; j < frame.length / 2; j++) {
        const magnitude = Math.abs(frame[j]);
        spectralSum += j * magnitude;
        magnitudeSum += magnitude;
      }
      
      const spectralCentroid = magnitudeSum > 0 ? spectralSum / magnitudeSum : 0;

      features.push({
        time: timePosition,
        rms,
        spectralCentroid,
        frame
      });
    }
  }

  return features;
};

export const detectBeatsFromFeatures = async (features, threshold = 0.03) => {
  const beats = [];
  let lastBeatTime = -1;
  const minBeatInterval = 0.1; // Minimum 100ms between beats

  // Enhanced beat detection with spectral analysis
  const energyHistory = [];
  const spectralHistory = [];
  const windowSize = 10; // Local energy window

  for (let i = 0; i < features.length; i++) {
    const feature = features[i];
    const { time, rms, spectralCentroid, fftData } = feature;
    
    // Store energy history for adaptive threshold
    energyHistory.push(rms);
    spectralHistory.push(spectralCentroid);
    
    if (energyHistory.length > windowSize) {
      energyHistory.shift();
      spectralHistory.shift();
    }
    
    // Calculate local average and variance
    const localAvgEnergy = energyHistory.reduce((sum, e) => sum + e, 0) / energyHistory.length;
    const localAvgSpectral = spectralHistory.reduce((sum, s) => sum + s, 0) / spectralHistory.length;
    
    const energyVariance = energyHistory.reduce((sum, e) => sum + Math.pow(e - localAvgEnergy, 2), 0) / energyHistory.length;
    const adaptiveThreshold = localAvgEnergy + Math.sqrt(energyVariance) * 1.5;
    
    // Enhanced beat detection with spectral analysis
    let isBeat = false;
    
    if (fftData && fftData.length > 0) {
      // Use FFT data for more accurate beat detection
      const lowFreqEnergy = fftData.slice(0, fftData.length / 8).reduce((sum, f) => sum + f.magnitude, 0);
      const midFreqEnergy = fftData.slice(fftData.length / 8, fftData.length / 4).reduce((sum, f) => sum + f.magnitude, 0);
      const highFreqEnergy = fftData.slice(fftData.length / 4, fftData.length / 2).reduce((sum, f) => sum + f.magnitude, 0);
      
      // Beat is detected if there's significant energy in low-mid frequencies
      isBeat = (lowFreqEnergy > adaptiveThreshold * 0.5) && 
               (midFreqEnergy > adaptiveThreshold * 0.3) &&
               (rms > Math.max(threshold, adaptiveThreshold * 0.6)) &&
               (time - lastBeatTime) > minBeatInterval;
    } else {
      // Fallback to traditional energy-based detection
      isBeat = rms > Math.max(threshold, adaptiveThreshold) && 
               spectralCentroid > 50 && 
               (time - lastBeatTime) > minBeatInterval;
    }
    
    if (isBeat) {
      beats.push(time);
      lastBeatTime = time;
    }
  }

  return beats;
};

export const createDefaultBeats = (duration, imageCount) => {
  const beats = [];
  const interval = Math.max(0.3, Math.min(1.5, duration / imageCount));
  
  for (let i = 0; i < Math.ceil(duration / interval); i++) {
    beats.push(i * interval);
  }
  
  return beats;
};
